import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useBooking } from "../contexts/BookingContext";
import { api } from "../config/api";
import AgeWarningModal from "../components/AgeWarningModal";
import { enrichScheduleMovies } from "../utils/scheduleEnrichment";
import "./CinemaPage.css";

const formatDurationLabel = (value) => {
  const mins = Number(value);
  if (Number.isFinite(mins) && mins > 0) {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (hours > 0) {
      return `${hours}h`;
    }
    return `${minutes}m`;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return "Duration TBA";
};

const parseAttributes = (attrString) => {
  const result = {};

  attrString
    .split("|")
    .filter(Boolean)
    .forEach((item) => {
      const [key, value] = item.split("=");
      if (key && value) {
        result[key.toLowerCase()] = value;
      }
    });

  return result;
};

const formatShowtimeLabel = (showtime) => {
  // Helper function to check if time already has AM/PM
  const hasAMPM = (timeString) => {
    if (!timeString) return false;
    const upper = timeString.toUpperCase().trim();
    return (
      upper.endsWith("AM") ||
      upper.endsWith("PM") ||
      upper.includes(" AM") ||
      upper.includes(" PM") ||
      upper.includes("AM ") ||
      upper.includes("PM ")
    );
  };

  // Helper function to convert 24-hour format to 12-hour format
  const convertTo12Hour = (timeString) => {
    // Extract just the time part (HH:MM) ignoring any existing AM/PM
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const minutesStr = minutes.toString().padStart(2, "0");
      return `${hours}:${minutesStr} ${ampm}`;
    }
    return timeString;
  };

  if (showtime.label) {
    // If already has AM/PM, return as is
    if (hasAMPM(showtime.label)) {
      return showtime.label;
    }
    // Convert 24-hour to 12-hour format
    return convertTo12Hour(showtime.label);
  }

  if (showtime.time && showtime.time !== "TBA") {
    // If already has AM/PM, return as is
    if (hasAMPM(showtime.time)) {
      return showtime.time;
    }
    // Convert 24-hour to 12-hour format
    return convertTo12Hour(showtime.time);
  }

  if (showtime.startDateTime) {
    const date = new Date(showtime.startDateTime);
    if (!Number.isNaN(date.getTime())) {
      // Convert 24-hour format to 12-hour format
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesStr = minutes.toString().padStart(2, "0");
      return `${hours}:${minutesStr} ${ampm}`;
    }
  }

  return "Time TBA";
};

const parseCastArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter(
      (entry) => typeof entry === "string" && entry.trim().length > 0,
    );
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  return [];
};

const CinemaPage = () => {
  const { cinemaId } = useParams();
  const navigate = useNavigate();
  const {
    setTheater,
    setQuantity,
    quantity,
    setVistaTransactionId,
    setSelectedSeats,
    setMovie,
    setShowtime,
  } = useBooking();

  useEffect(() => {
    setQuantity(null); // ✅ reset when entering cinema page
  }, []);

  const [cinema, setCinema] = useState(null);
  const [formattedAmenities, setFormattedAmenities] = useState([]);
  const [movies, setMovies] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [scheduleCache, setScheduleCache] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMovies, setExpandedMovies] = useState(new Set());
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [error, setError] = useState("");
  const [isInitializingBooking, setIsInitializingBooking] = useState(false);
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [pendingShowtime, setPendingShowtime] = useState(null);
  const [pendingMovie, setPendingMovie] = useState(null);
  const [showAgeWarning, setShowAgeWarning] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  const formatCinemaAddress = useCallback((cinemaDetails) => {
    if (!cinemaDetails) {
      return "";
    }

    const parts = [
      typeof cinemaDetails.address === "string"
        ? cinemaDetails.address.trim()
        : "",
      typeof cinemaDetails.city === "string" ? cinemaDetails.city.trim() : "",
      typeof cinemaDetails.state === "string" ? cinemaDetails.state.trim() : "",
      typeof cinemaDetails.pincode === "string"
        ? cinemaDetails.pincode.trim()
        : "",
    ].filter(Boolean);

    return parts.join(", ");
  }, []);

  const parseAmenities = useCallback((amenitiesRaw) => {
    if (!amenitiesRaw) {
      return [];
    }

    if (Array.isArray(amenitiesRaw)) {
      return amenitiesRaw
        .filter((item) => typeof item === "string")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    if (typeof amenitiesRaw === "string") {
      try {
        const parsed = JSON.parse(amenitiesRaw);
        if (Array.isArray(parsed)) {
          return parsed
            .filter((item) => typeof item === "string")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      } catch {
        return amenitiesRaw
          .replace(/^\{/, "")
          .replace(/\}$/, "")
          .split(",")
          .map((item) => item.replace(/["']/g, "").trim())
          .filter(Boolean);
      }
    }

    return [];
  }, []);

  // const fetchSchedule = useCallback(     //commented out for days restriction
  //   async (dateStr) => {
  //     if (!cinemaId) return;

  //     try {
  //       setLoadingSchedule(true);
  //       setError('');

  //       // Check if cinemaId is a UUID format (contains hyphens) or a Vista cinema ID
  //       // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  //       const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(cinemaId);

  //       let databaseCinemaId = cinemaId;

  //       // If it's not a UUID, it's likely a Vista cinema ID - find the database UUID
  //       if (!isUUID) {
  //         try {
  //           // Fetch all cinemas and find the one with matching Vista cinema ID
  //           const allCinemas = await api.getCinemas();
  //           const matchingCinema = allCinemas.find(c =>
  //             c.vistaCinemaId === cinemaId ||
  //             c.vistacinemaid === cinemaId ||
  //             c.vista_cinema_id === cinemaId ||
  //             String(c.vistaCinemaId) === String(cinemaId) ||
  //             String(c.vistacinemaid) === String(cinemaId) ||
  //             String(c.vista_cinema_id) === String(cinemaId)
  //           );

  //           if (matchingCinema && matchingCinema.id) {
  //             databaseCinemaId = matchingCinema.id;
  //           } else {
  //             throw new Error(`Cinema with Vista ID ${cinemaId} not found`);
  //           }
  //         } catch (findError) {
  //           console.error('Error finding cinema by Vista ID:', findError);
  //           setError('Cinema not found');
  //           setCinema(null);
  //           setMovies([]);
  //           setFormattedAmenities([]);
  //           setLoadingSchedule(false);
  //           return;
  //         }
  //       }

  //       const schedule = await api.getCinemaSchedule(databaseCinemaId, dateStr);

  //       if (!schedule || !schedule.cinema) {
  //         setCinema(null);
  //         setMovies([]);
  //         setFormattedAmenities([]);
  //         setError('Cinema not found');
  //         return;
  //       }

  //       const parsedAmenities = parseAmenities(schedule.cinema.amenities);

  //       const normalizedCinema = {
  //         ...schedule.cinema,
  //         amenities: parsedAmenities,
  //         displayAddress: formatCinemaAddress(schedule.cinema),
  //         landmark:
  //           schedule.cinema.location &&
  //           schedule.cinema.location !== schedule.cinema.address
  //             ? schedule.cinema.location
  //             : null,
  //       };

  //       setCinema(normalizedCinema);
  //       setFormattedAmenities(parsedAmenities);
  //       setMovies(Array.isArray(schedule.movies) ? schedule.movies : []);
  //       setTheater(normalizedCinema);
  //     } catch (err) {
  //       console.error('Error fetching cinema schedule:', err);
  //       setCinema(null);
  //       setMovies([]);
  //       setFormattedAmenities([]);
  //       setError('Failed to load cinema schedule');
  //     } finally {
  //       setLoadingSchedule(false);
  //     }
  //   },
  //   [cinemaId, formatCinemaAddress, parseAmenities, setTheater]
  // );

  // useEffect(() => {
  //   fetchSchedule(selectedDate);
  // }, [fetchSchedule, selectedDate]);

  // const availableDates = useMemo(() => {
  //   const dates = [];
  //   const today = new Date();

  //   for (let i = 0; i < 5; i++) {
  //     const date = new Date(today);
  //     date.setDate(today.getDate() + i);
  //     const dateStr = date.toISOString().split('T')[0];
  //     dates.push({
  //       date,
  //       dateStr,
  //       label:
  //         i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short' }),
  //       fullLabel:
  //         i === 0
  //           ? `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} Today`
  //           : i === 1
  //             ? `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} Tomorrow`
  //             : `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${date.toLocaleDateString('en-US', { weekday: 'short' })}`,
  //     });
  //   }

  //   return dates;
  // }, []);

  useEffect(() => {
    if (!cinemaId) return;
    const discover = async () => {
      try {
        setLoadingSchedule(true);
        setError("");

        // Resolve database UUID if cinemaId is a Vista ID
        const isUUID =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            cinemaId,
          );
        let databaseCinemaId = cinemaId;
        if (!isUUID) {
          const allCinemas = await api.getCinemas();
          const match = allCinemas.find(
            (c) =>
              String(c.vistaCinemaId) === String(cinemaId) ||
              String(c.vistacinemaid) === String(cinemaId) ||
              String(c.vista_cinema_id) === String(cinemaId),
          );
          if (match?.id) {
            databaseCinemaId = match.id;
          } else {
            setError("Cinema not found");
            setLoadingSchedule(false);
            return;
          }
        }

        const candidates = Array.from({ length: 14 }, (_, offset) => {
          const d = new Date();
          d.setDate(d.getDate() + offset);
          const dateStr = d.toLocaleDateString('en-CA'); // local time, no UTC shift
          const label = offset === 0
            ? 'Today'
            : offset === 1
              ? 'Tomorrow'
              : d.toLocaleDateString('en-US', { weekday: 'short' });
          return { date: d, dateStr, label };
        });

        const results = await Promise.all(
          candidates.map(async (candidate) => {
            try {
              const schedule = await api.getCinemaSchedule(
                databaseCinemaId,
                candidate.dateStr,
              );
              const moviesForDate = Array.isArray(schedule?.movies)
                ? schedule.movies
                : [];
              const hasShows = moviesForDate.some(
                (m) => Array.isArray(m.showtimes) && m.showtimes.length > 0,
              );
              return { candidate, schedule, movies: moviesForDate, hasShows };
            } catch {
              return { candidate, schedule: null, movies: [], hasShows: false };
            }
          }),
        );

        const validResults = results.filter((r) => r.hasShows);

        if (validResults.length === 0) {
          setAvailableDates([]);
          setMovies([]);
          setCinema(null);
          setError("No shows available in the next 5 days.");
          setLoadingSchedule(false);
          return;
        }

        let dbMovies = [];
        try {
          dbMovies = await api.getMovies({ limit: 100 });
        } catch (e) {
          console.warn("Could not fetch DB movies:", e);
        }

        const isValid = (val) => {
          if (!val) return false;
          const v = String(val).toLowerCase().trim();
          return (
            v !== "duration tba" && v !== "tba" && v !== "nr" && v !== "na"
          );
        };

        const enrichWithPosters = (scheduleMovies) =>
          enrichScheduleMovies(scheduleMovies, dbMovies);
        const cache = {};
        validResults.forEach((r) => {
          cache[r.candidate.dateStr] = {
            ...r,
            movies: enrichWithPosters(r.movies),
          };
        });
        setScheduleCache(cache);
        setAvailableDates(validResults.map((r) => r.candidate));

        const first = validResults[0];
        setSelectedDate(first.candidate.dateStr);
        setMovies(enrichWithPosters(first.movies));

        if (first.schedule?.cinema) {
          const parsedAmenities = parseAmenities(
            first.schedule.cinema.amenities,
          );
          const normalizedCinema = {
            ...first.schedule.cinema,
            amenities: parsedAmenities,
            displayAddress: formatCinemaAddress(first.schedule.cinema),
            landmark:
              first.schedule.cinema.location &&
              first.schedule.cinema.location !== first.schedule.cinema.address
                ? first.schedule.cinema.location
                : null,
          };
          setCinema(normalizedCinema);
          setFormattedAmenities(parsedAmenities);
          setTheater(normalizedCinema);
        }
      } catch (err) {
        console.error("Error fetching cinema schedule:", err);
        setError("Failed to load cinema schedule");
      } finally {
        setLoadingSchedule(false);
      }
    };
    discover();
  }, [cinemaId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredMovies = useMemo(() => {
    const moviesWithShowtimes = movies.filter(
      (movie) => Array.isArray(movie.showtimes) && movie.showtimes.length > 0,
    );

    if (!searchQuery) {
      return moviesWithShowtimes;
    }

    const query = searchQuery.toLowerCase();
    return moviesWithShowtimes.filter((movie) => {
      const title = (movie.title || "").toLowerCase();
      const genre = (movie.genre || "").toLowerCase();
      const language = (movie.language || "").toLowerCase();
      return (
        title.includes(query) ||
        genre.includes(query) ||
        language.includes(query)
      );
    });
  }, [movies, searchQuery]);

  const getShowtimeStatus = useMemo(() => {
    return (showtime) => {
      const now = new Date();
      const showDateTime = showtime.startDateTime
        ? new Date(showtime.startDateTime)
        : new Date(`${showtime.date} ${showtime.time}`);

      if (!Number.isNaN(showDateTime.getTime()) && showDateTime < now) {
        return "lapsed";
      }

      // const seed = (
      //   showtime.id || `${showtime.movieId}-${showtime.label || showtime.time}`
      // )
      //   .split("")
      //   .reduce((acc, char) => {
      //     acc = (acc << 5) - acc + char.charCodeAt(0);
      //     return acc & acc;
      //   }, 0);
      // const bookingPercentage = Math.abs(seed) % 100;

      // if (bookingPercentage >= 100) return "sold-out";
      // if (bookingPercentage >= 70) return "filling-fast";
      let totalSeats = parseInt(showtime.totalSeats);
      let availableSeats = parseInt(showtime.availableSeats);
      let soldSeats =  totalSeats - availableSeats;
      let occupiedPercent = totalSeats > 0
      ? Math.max(0, Math.min(100, Math.round((soldSeats / totalSeats) * 100)))
      : 0;
      if (availableSeats === 0) return "sold-out";
      if (occupiedPercent >= 55) return "filling-fast";

      return "available";
    };
  }, []);

  const handleDateChange = (dateInfo) => {
    setSelectedDate(dateInfo.dateStr);
    setExpandedMovies(new Set());
    const cached = scheduleCache[dateInfo.dateStr];
    if (cached) {
      setMovies(cached.movies);
    }
  };

  const toggleMovieExpansion = (movieId) => {
    const isExpanded = expandedMovies.has(movieId);
    if (isExpanded) {
      setExpandedMovies(new Set());
    } else {
      setExpandedMovies(new Set([movieId]));
    }
  };

  const handleShowtimeSelect = async (movie, showtime) => {
    if (!cinema) return;

    // Check age rating first, before quantity selection
    const ageRating = movie.ageRating || movie.rating || "";
    if (ageRating.toUpperCase() === "A") {
      setPendingBookingData({ movie, showtime });
      setShowAgeWarning(true);
      return;
    }

    // After age check passes, check if quantity is selected
    if (!quantity || quantity < 1 || quantity > 10) {
      // Show quantity selection modal
      setPendingShowtime(showtime);
      setPendingMovie(movie);
      setShowQuantityModal(true);
      return;
    }

    // Proceed with booking initialization
    await proceedWithBooking(movie, showtime);
  };

  const proceedWithBooking = async (movie, showtime) => {
    setTheater(cinema);

    // Reset timer and seat selection when switching shows
    setSelectedSeats([]);
    // Clear previous transaction ID when switching shows to prevent using wrong transaction ID
    setVistaTransactionId(null);

    // Initialize Vista booking before navigating to seat selection
    try {
      setIsInitializingBooking(true);

      // Get cinemaId and sessionId from showtime/theater
      // sessionId is the Vista session ID - it's stored in the showtime.id field (which is the Vista sessionId)
      const vistaCinemaId = cinema.vistaCinemaId || cinema.id;
      // The showtime.id is the Vista sessionId (set during show mapping)
      const sessionId =
        showtime.id || showtime.sessionId || showtime.vistaShowId;

      if (!vistaCinemaId || !sessionId) {
        console.error(
          "Missing vistaCinemaId or sessionId for booking initialization",
        );
        showError("Unable to initialize booking. Please try again.");
        setIsInitializingBooking(false);
        return;
      }

      // Initialize Vista booking
      const bookingData = await api.initVistaBooking(vistaCinemaId, sessionId);

      if (bookingData && bookingData.transactionId) {
        // Store Vista transaction ID and session ID together in context for validation
        setVistaTransactionId({
          transactionId: bookingData.transactionId,
          sessionId: sessionId, // Store the session ID this transaction belongs to
        });
        // console.log("Vista booking initialized", {
        //   transactionId: bookingData.transactionId,
        //   sessionId: sessionId,
        // });

        // Store all data in booking context (same as booking page)
        setMovie(movie);
        setTheater(cinema);

        // Ensure showtime has the correct structure with pricing data
        // The showtime from cinema schedule should already have price structure, but ensure it's complete
        const completeShowtime = {
          ...showtime,
          id: sessionId, // Ensure sessionId is set
          sessionId: sessionId,
          vistaShowId: sessionId,
          date: selectedDate,
          // Ensure price structure exists - if not, we'll need to fetch it or use default
          // The showtime should already have price from the API response
        };

        setShowtime(completeShowtime);

        navigate("/seat-selection", {
          state: {
            movie,
            showtime: completeShowtime,
            theater: cinema,
            selectedDate: selectedDate,
            vistaTransactionId: bookingData.transactionId,
            vistaSessionId: sessionId,
          },
        });
      } else {
        throw new Error(
          "Failed to get transaction ID from booking initialization",
        );
      }
    } catch (error) {
      console.error("Error initializing Vista booking:", error);
      showError(
        `Failed to initialize booking: ${error.message || "Please try again."}`,
      );
    } finally {
      setIsInitializingBooking(false);
    }
  };

  const handleQuantitySelect = () => {
    if (quantity && quantity >= 1 && quantity <= 10) {
      setShowQuantityModal(false);
      if (pendingShowtime && pendingMovie) {
        // Age warning should have been shown before quantity modal,
        // so proceed directly to booking
        proceedWithBooking(pendingMovie, pendingShowtime);
      }
      setPendingShowtime(null);
      setPendingMovie(null);
    }
  };

  // Handle age warning confirmation
  const handleAgeWarningConfirm = async () => {
    setShowAgeWarning(false);
    if (pendingBookingData) {
      const { movie, showtime } = pendingBookingData;
      setPendingBookingData(null);

      // After age warning is confirmed, check quantity
      if (!quantity || quantity < 1 || quantity > 10) {
        // Show quantity selection modal
        setPendingShowtime(showtime);
        setPendingMovie(movie);
        setShowQuantityModal(true);
      } else {
        // Proceed with booking initialization
        await proceedWithBooking(movie, showtime);
      }
    }
  };

  // Handle age warning close
  const handleAgeWarningClose = () => {
    setShowAgeWarning(false);
    setPendingBookingData(null);
  };

  if (loadingSchedule && !cinema) {
    return (
      <div className="container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading cinema...</span>
          </div>
          <p className="mt-3">Loading cinema details...</p>
        </div>
      </div>
    );
  }

  if (!cinema) {
    return (
      <div className="container">
        <div className="text-center py-5">
          <h2>{error || "Cinema not found"}</h2>
          <button className="btn btn-primary" onClick={() => navigate("/")}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cinema-details-banner">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="cinema-info">
                <h1 className="cinema-name">{cinema.name}</h1>
                <p className="cinema-location">
                  <i className="fas fa-map-marker-alt"></i>
                  {cinema.displayAddress || formatCinemaAddress(cinema)}
                </p>
                {(cinema.screens || cinema.totalSeats) && (
                  <div className="cinema-meta">
                    <span>
                      <i className="fas fa-film"></i> {cinema.screens ?? "N/A"}{" "}
                      Screens
                    </span>
                    <span>
                      <i className="fas fa-chair"></i>{" "}
                      {cinema.totalSeats ?? "N/A"} Seats
                    </span>
                  </div>
                )}
                {(cinema.phone || cinema.email) && (
                  <div className="cinema-contact">
                    {cinema.phone && (
                      <span>
                        <i className="fas fa-phone"></i> {cinema.phone}
                      </span>
                    )}
                    {cinema.email && (
                      <span>
                        <i className="fas fa-envelope"></i> {cinema.email}
                      </span>
                    )}
                  </div>
                )}
                {formattedAmenities.length > 0 && (
                  <div className="cinema-features">
                    {formattedAmenities.map((amenity, index) => (
                      <div key={index} className="feature-item">
                        <i className="fas fa-check-circle"></i>
                        <span>{amenity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-4">
              <div className="cinema-visual">
                <div className="cinema-icon">
                  <i className="fas fa-film"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="date-search">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-xl-5">
              <div className="date-picker-wrapper">
                <div className="date-picker">
                  {availableDates.map((dateInfo, index) => (
                    <div
                      key={index}
                      className={`date-item ${selectedDate === dateInfo.dateStr ? "active" : ""}`}
                      onClick={() => handleDateChange(dateInfo)}
                    >
                      <span className="month">
                        {dateInfo.date
                          .toLocaleDateString("en-US", { month: "short" })
                          .toUpperCase()}
                      </span>
                      <span className="day">
                        {dateInfo.date.getDate().toString().padStart(2, "0")}
                      </span>
                      <span className="weekday">
                        {dateInfo.date
                          .toLocaleDateString("en-US", { weekday: "short" })
                          .toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-xl-7 cinema-top-controls">
              <div className="top-right-tools">
                {/* LEGEND */}
                <div className="legend">
                  <div className="legend-item">
                    <span className="legend-color available"></span>
                    <span>Available</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color filling-fast"></span>
                    <span>Filling Fast</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color sold-out"></span>
                    <span>Sold Out</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color lapsed"></span>
                    <span>Lapsed</span>
                  </div>

                  <div className="legend-icons">
                    <span className="icon-item">
                      <i className="fas fa-closed-captioning"></i>
                      <span>Subtitle</span>
                    </span>
                    <span className="icon-item">
                      <i className="fas fa-wheelchair"></i>
                      <span>Accessibility</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="movies" className="movies-section">
        <div className="container">
          {loadingSchedule ? (
            <div className="skeleton-loading">
              {[1, 2, 3].map((index) => (
                <div key={index} className="skeleton-movie-card">
                  <div className="skeleton-poster"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-meta">
                      <div className="skeleton-badge"></div>
                      <div className="skeleton-badge"></div>
                      <div className="skeleton-badge"></div>
                      <div className="skeleton-badge"></div>
                    </div>
                    <div className="skeleton-details">
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line"></div>
                      <div className="skeleton-line short"></div>
                    </div>
                  </div>
                  <div className="skeleton-actions">
                    <div className="skeleton-button"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredMovies.length === 0 ? (
            <div className="no-movies">
              <h3>No movies found</h3>
              <p>Try adjusting your search or select a different date.</p>
            </div>
          ) : (
            filteredMovies.map((movie) => {
              const showtimes = Array.isArray(movie.showtimes)
                ? movie.showtimes
                : [];
              const isExpanded = expandedMovies.has(movie.id);
              const movieCastArray = parseCastArray(movie.cast);
              const posterImage =
                movie.image ||
                movie.poster ||
                movie.banner ||
                movie.posterUrl ||
                movie.posterurl ||
                "/assets/img/movie-placeholder.png";
              const movieDuration = formatDurationLabel(movie.duration);
              const movieRating = movie.ageRating || movie.rating || "NR";
              const movieLanguage = movie.language || "";
              const movieGenre = movie.genre
                ? String(movie.genre)
                    .replace(/[{}"]/g, "")
                    .split(",")
                    .map((g) => g.trim())
                    .filter(Boolean)
                    .join(", ")
                : "";

              return (
                <div key={movie.id} className="movie-card">
                  <div className="movie-poster">
                    <img src={posterImage} alt={movie.title} />
                  </div>
                  <div className="movie-details">
                    <h3 className="movie-title">
                      {movie.title.replace(/\(.*?\)/g, "").trim()}
                    </h3>
                    <div className="movie-info">
                      <span className="rating">{movieRating}</span>
                      <span className="duration">{movieDuration}</span>
                      <span className="language">{movieLanguage}</span>
                      {movieGenre && (
                        <span className="genre">{movieGenre}</span>
                      )}
                    </div>

                    <div className="showtimes-section">
                      {movieCastArray.length > 0 && (
                        <div className="showtimes-starring">
                          <span className="label">Starring:</span>
                          <span className="value">
                            {movieCastArray.join(", ")}
                          </span>
                        </div>
                      )}
                      {showtimes.length > 0 ? (
                        <div className="show-times">
                          {showtimes.map((showtime) => {
                            const status = getShowtimeStatus(showtime);
                            const isSoldOut = status === "sold-out";
                            const isDisabled = isInitializingBooking || isSoldOut;                            
                            const displayTime = formatShowtimeLabel(showtime);
                            const screenAttributes =
                              parseAttributes(showtime.screenAttributes)
                                ?.screenattributes || "";
                            const screenName = showtime.screenName || "Screen";
                            const screenType = showtime.screenType
                              ? showtime.screenType.toUpperCase()
                              : "REGULAR";

                            return (
                              <div
                                key={showtime.id}
                                // className={`show-time-item show-time-${status} ${isInitializingBooking ? "disabled" : ""}`}
                                className={`show-time-item show-time-${status} ${isDisabled ? "disabled" : ""}`}
                                // onClick={() =>
                                //   !isInitializingBooking &&
                                //   handleShowtimeSelect(movie, showtime)
                                // }
                                onClick={(e) => {
                                  if (isDisabled) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  handleShowtimeSelect(movie, showtime);
                                }}
                                // style={{
                                //   opacity: isInitializingBooking ? 0.6 : 1,
                                //   cursor: isInitializingBooking
                                //     ? "not-allowed"
                                //     : "pointer",
                                // }}
                                style={{
                                  opacity: isDisabled ? 0.6 : 1,
                                  cursor: isDisabled ? "not-allowed" : "pointer",
                                }}
                              >
                                <div className="show-time-border"></div>
                                <div className="show-time-content">
                                  <div className="show-time">{displayTime}</div>
                                  {/* <div className="show-type">{screenType}</div> */}
                                  <div className="show-screen">{screenName}</div>
                                  <div className="show-screen-attributes">
                                    {screenAttributes}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="no-showtimes">
                          <p>No showtimes available for this date.</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="movie-actions">
                    <button
                      className="expand-btn"
                      onClick={() => toggleMovieExpansion(movie.id)}
                      aria-label={
                        isExpanded ? "Collapse details" : "Expand details"
                      }
                    >
                      {isExpanded ? "−" : "+"}
                    </button>
                  </div>

                  {isExpanded && (
                    <div className="movie-details-accordion-content">
                      <div className="movie-meta-details">
                        <div className="meta-item">
                          <span className="label">Director:</span>
                          <span className="value">
                            {movie.director || "N/A"}
                          </span>
                        </div>
                        <div className="meta-item">
                          <span className="label">Starring:</span>
                          <span className="value">
                            {movieCastArray.length > 0
                              ? movieCastArray.join(", ")
                              : "N/A"}
                          </span>
                        </div>
                        <div className="meta-item">
                          <span className="label">Synopsis:</span>
                          <span className="value">
                            {movie.description || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Quantity Selection Modal */}
      {showQuantityModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowQuantityModal(false);
            setPendingShowtime(null);
            setPendingMovie(null);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>Select Quantity</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowQuantityModal(false);
                  setPendingShowtime(null);
                  setPendingMovie(null);
                }}
              >
                <span>&times;</span>
              </button>
            </div>
            <div className="modal-body">
              <p>Please select the number of tickets before proceeding.</p>
              <div className="form-group">
                <label htmlFor="modal-quantity">Select Qty</label>
                <select
                  id="modal-quantity"
                  className="form-control"
                  value={quantity || ""}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                >
                  <option value="">Select Qty</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((qty) => (
                    <option key={qty} value={qty}>
                      {qty}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowQuantityModal(false);
                  setPendingShowtime(null);
                  setPendingMovie(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleQuantitySelect}
                disabled={!quantity || quantity < 1 || quantity > 10}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {showErrorPopup && (
        <div
          className="error-popup-overlay"
          onClick={() => setShowErrorPopup(false)}
        >
          <div
            className="error-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="error-popup-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <div className="error-popup-message">{errorMessage}</div>
            <button
              className="error-popup-button"
              onClick={() => setShowErrorPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Age Warning Modal */}
      <AgeWarningModal
        isOpen={showAgeWarning}
        onClose={handleAgeWarningClose}
        onConfirm={handleAgeWarningConfirm}
      />
    </>
  );
};

export default CinemaPage;
